/**
 * Campaign block renderer — email-safe table HTML.
 * Mirror: functions/campaignBlocks.js (keep identical logic).
 */

export const BLOCK_TYPES = [
  "hero",
  "heading",
  "greeting",
  "text",
  "section",
  "facts",
  "button",
  "image",
  "divider",
  "note",
];

export const MAX_BLOCKS = 40;
export const MAX_RICH_HTML = 20000;
export const MAX_TEXT = 500;
export const MAX_URL = 2000;
export const MAX_FACTS = 3;

export const CAMPAIGN_COLORS = {
  primary: "#2AF598",
  tertiary: "#009658",
  title: "#000000",
  body: "#353535",
  muted: "#AAAAAA",
  pageBg: "#F8FAFC",
  card: "#FFFFFF",
  buttonText: "#000000",
  divider: "#e8ebe8",
};

const FONT_TITLE = "Georgia,'Times New Roman',serif";
const FONT_BODY = "'Helvetica Neue',Helvetica,Arial,sans-serif";

function uid() {
  return `b_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

export function escapeHtml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function sanitizeUrl(url) {
  const u = String(url || "").trim();
  if (!u) return "";
  if (/^https:\/\//i.test(u) || /^mailto:/i.test(u) || /^tel:/i.test(u)) return u;
  return "";
}

export function isEmptyRichHtml(html) {
  const s = String(html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return !s;
}

const RICH_STYLES = {
  p: `margin:0 0 12px 0;font-family:${FONT_BODY};font-size:15px;line-height:1.65;color:${CAMPAIGN_COLORS.body};`,
  ul: `margin:0 0 12px 20px;padding:0;font-family:${FONT_BODY};font-size:15px;line-height:1.65;color:${CAMPAIGN_COLORS.body};`,
  ol: `margin:0 0 12px 20px;padding:0;font-family:${FONT_BODY};font-size:15px;line-height:1.65;color:${CAMPAIGN_COLORS.body};`,
  li: "margin:0 0 6px 0;",
  strong: `font-weight:bold;color:${CAMPAIGN_COLORS.title};`,
  b: `font-weight:bold;color:${CAMPAIGN_COLORS.title};`,
  em: "font-style:italic;",
  i: "font-style:italic;",
  u: "text-decoration:underline;",
};

const NOTE_STYLES = {
  ...RICH_STYLES,
  p: `margin:0 0 6px 0;font-family:${FONT_BODY};font-size:12px;line-height:1.55;color:${CAMPAIGN_COLORS.body};`,
  ul: `margin:0 0 6px 18px;padding:0;font-family:${FONT_BODY};font-size:12px;line-height:1.55;color:${CAMPAIGN_COLORS.body};`,
  ol: `margin:0 0 6px 18px;padding:0;font-family:${FONT_BODY};font-size:12px;line-height:1.55;color:${CAMPAIGN_COLORS.body};`,
  li: "margin:0 0 3px 0;",
};

const ALLOWED_RICH = /^(p|br|strong|b|em|i|u|a|ul|ol|li|span)$/i;

export function sanitizeRichHtml(html, styles = RICH_STYLES) {
  let s = String(html || "");
  s = s.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "");
  s = s.replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "");
  s = s.replace(/<!--[\s\S]*?-->/g, "");
  s = s.replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  s = s.replace(/href\s*=\s*(["']?)\s*javascript:[^"'>\s]*/gi, "href=$1#");
  s = s.replace(/<\/?([a-zA-Z0-9]+)([^>]*)>/g, (match, tag) => {
    if (!ALLOWED_RICH.test(tag)) return "";
    const t = tag.toLowerCase();
    if (t === "br") return "<br />";
    const closing = match.startsWith("</");
    if (closing) return `</${t}>`;
    if (t === "a") {
      const hrefMatch = match.match(/href\s*=\s*["']([^"']+)["']/i);
      const href = hrefMatch ? sanitizeUrl(hrefMatch[1].replace(/&amp;/gi, "&")) : "";
      if (!href) return "<span>";
      return `<a href="${escapeHtml(href)}" style="color:${CAMPAIGN_COLORS.tertiary};text-decoration:underline;" target="_blank">`;
    }
    const st = styles[t];
    return st ? `<${t} style="${st}">` : `<${t}>`;
  });
  if (s.length > MAX_RICH_HTML) s = s.slice(0, MAX_RICH_HTML);
  return s;
}

export function applyPersonalization(html, ctx = {}) {
  const full = String(ctx.full_name || ctx.first_name || "Client").trim() || "Client";
  const first = String(ctx.first_name || full.split(/\s+/)[0] || "Client").trim() || "Client";
  return String(html || "")
    .replace(/\{\{\s*first_name\s*\}\}/gi, escapeHtml(first))
    .replace(/\{\{\s*full_name\s*\}\}/gi, escapeHtml(full));
}

function defaultFacts() {
  return [
    { label: "Yield", value: "", note: "" },
    { label: "USDC", value: "", note: "" },
    { label: "Hours", value: "", note: "" },
  ];
}

export function createBlock(type) {
  const t = BLOCK_TYPES.includes(type) ? type : "text";
  const base = { id: uid(), type: t };
  switch (t) {
    case "hero":
      return { ...base, url: "", alt: "Fiddux", link: "" };
    case "heading":
      return { ...base, eyebrow: "", title: "", lead: "" };
    case "greeting":
      return { ...base, enabled: true };
    case "text":
      return { ...base, html: "<p></p>" };
    case "section":
      return { ...base, title: "", html: "<p></p>" };
    case "facts":
      return { ...base, items: defaultFacts() };
    case "button":
      return { ...base, label: "Open Fiddux", url: "https://fiddux.com", align: "center" };
    case "image":
      return { ...base, url: "", alt: "", caption: "" };
    case "divider":
      return { ...base };
    case "note":
      return { ...base, html: "<p></p>" };
    default:
      return { ...base, html: "<p></p>" };
  }
}

export function cloneBlock(block) {
  return JSON.parse(JSON.stringify({ ...block, id: uid() }));
}

function clipText(value, max = MAX_TEXT) {
  return String(value || "").slice(0, max);
}

export function normalizeBlock(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = BLOCK_TYPES.includes(raw.type) ? raw.type : null;
  if (!type) return null;
  const block = createBlock(type);
  block.id = typeof raw.id === "string" && raw.id ? raw.id.slice(0, 64) : block.id;
  switch (type) {
    case "hero":
      block.url = sanitizeUrl(raw.url);
      block.alt = clipText(raw.alt, 120);
      block.link = sanitizeUrl(raw.link);
      break;
    case "heading":
      block.eyebrow = clipText(raw.eyebrow, 80);
      block.title = clipText(raw.title, 200);
      block.lead = clipText(raw.lead, 800);
      break;
    case "greeting":
      block.enabled = raw.enabled !== false;
      break;
    case "text":
    case "note":
      block.html = sanitizeRichHtml(raw.html);
      break;
    case "section":
      block.title = clipText(raw.title, 80);
      block.html = sanitizeRichHtml(raw.html);
      break;
    case "facts": {
      const items = Array.isArray(raw.items) ? raw.items : [];
      block.items = items.slice(0, MAX_FACTS).map((it) => ({
        label: clipText(it?.label, 40),
        value: clipText(it?.value, 80),
        note: clipText(it?.note, 200),
      }));
      if (!block.items.length) block.items = defaultFacts().slice(0, 1);
      break;
    }
    case "button":
      block.label = clipText(raw.label, 60) || "Open Fiddux";
      block.url = sanitizeUrl(raw.url) || "https://fiddux.com";
      block.align = raw.align === "left" ? "left" : "center";
      break;
    case "image":
      block.url = sanitizeUrl(raw.url);
      block.alt = clipText(raw.alt, 120);
      block.caption = clipText(raw.caption, 200);
      break;
    default:
      break;
  }
  return block;
}

export function validateBlocks(blocks) {
  if (!Array.isArray(blocks)) {
    return { ok: false, error: "blocks must be an array" };
  }
  if (blocks.length > MAX_BLOCKS) {
    return { ok: false, error: `Maximum ${MAX_BLOCKS} blocks` };
  }
  const normalized = [];
  for (const raw of blocks) {
    const block = normalizeBlock(raw);
    if (!block) {
      return { ok: false, error: "Unknown or invalid block" };
    }
    if (typeof raw?.url === "string" && raw.url && !sanitizeUrl(raw.url)) {
      return { ok: false, error: "Image and button URLs must be https" };
    }
    if (typeof raw?.link === "string" && raw.link && !sanitizeUrl(raw.link)) {
      return { ok: false, error: "Links must be https, mailto, or tel" };
    }
    normalized.push(block);
  }
  return { ok: true, blocks: normalized };
}

function row(inner, padding) {
  return `<tr><td style="padding:${padding};">${inner}</td></tr>`;
}

function renderHero(block) {
  if (!block.url) return "";
  const img = `<img src="${escapeHtml(block.url)}" alt="${escapeHtml(block.alt || "Fiddux")}" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;" />`;
  const inner = block.link
    ? `<a href="${escapeHtml(block.link)}" target="_blank" style="text-decoration:none;">${img}</a>`
    : img;
  return row(inner, "0");
}

function renderHeading(block) {
  const parts = [];
  if (block.eyebrow) {
    parts.push(
      `<p style="margin:0 0 10px 0;font-family:${FONT_BODY};font-size:11px;letter-spacing:2.5px;color:${CAMPAIGN_COLORS.tertiary};font-weight:bold;text-transform:uppercase;">${escapeHtml(block.eyebrow)}</p>`
    );
  }
  if (block.title) {
    parts.push(
      `<p style="margin:0 0 12px 0;font-family:${FONT_TITLE};font-size:26px;line-height:1.3;color:${CAMPAIGN_COLORS.title};font-weight:normal;">${escapeHtml(block.title)}</p>`
    );
  }
  if (block.lead) {
    parts.push(
      `<p style="margin:0 0 8px 0;font-family:${FONT_BODY};font-size:15px;line-height:1.65;color:${CAMPAIGN_COLORS.body};">${escapeHtml(block.lead)}</p>`
    );
  }
  if (!parts.length) return "";
  return row(parts.join(""), "12px 40px 16px 40px");
}

function renderGreeting(block) {
  if (block.enabled === false) return "";
  return row(
    `<p style="margin:0;font-family:${FONT_BODY};font-size:16px;line-height:1.5;color:${CAMPAIGN_COLORS.title};">Dear {{first_name}},</p>`,
    "8px 40px 16px 40px"
  );
}

function renderText(block) {
  if (isEmptyRichHtml(block.html)) return "";
  return row(sanitizeRichHtml(block.html), "4px 40px 12px 40px");
}

function renderNote(block) {
  if (isEmptyRichHtml(block.html)) return "";
  return row(sanitizeRichHtml(block.html, NOTE_STYLES), "4px 40px 12px 40px");
}

function renderSection(block) {
  if (!block.title && isEmptyRichHtml(block.html)) return "";
  const title = block.title
    ? `<p style="margin:0 0 8px 0;font-family:${FONT_BODY};font-size:13px;font-weight:bold;letter-spacing:1.5px;color:${CAMPAIGN_COLORS.title};text-transform:uppercase;">${escapeHtml(block.title)}</p>`
    : "";
  const body = isEmptyRichHtml(block.html)
    ? ""
    : `<div style="margin:0;font-family:${FONT_BODY};font-size:15px;line-height:1.65;color:${CAMPAIGN_COLORS.body};">${sanitizeRichHtml(block.html)}</div>`;
  const inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0;">
<tr>
<td style="width:3px;background-color:${CAMPAIGN_COLORS.primary};font-size:0;line-height:0;">&nbsp;</td>
<td style="padding:4px 0 4px 18px;">${title}${body}</td>
</tr>
</table>`;
  return row(inner, "4px 40px 16px 40px");
}

function renderFacts(block) {
  const items = (block.items || []).filter((it) => it.label || it.value || it.note);
  if (!items.length) return "";
  const width = Math.floor(100 / items.length);
  const cells = items
    .map((it) => {
      const label = it.label
        ? `<p style="margin:0 0 6px 0;font-family:${FONT_BODY};font-size:11px;letter-spacing:1.5px;color:${CAMPAIGN_COLORS.tertiary};font-weight:bold;text-transform:uppercase;">${escapeHtml(it.label)}</p>`
        : "";
      const value = it.value
        ? `<p style="margin:0 0 6px 0;font-family:${FONT_TITLE};font-size:20px;line-height:1.3;color:${CAMPAIGN_COLORS.title};">${escapeHtml(it.value)}</p>`
        : "";
      const note = it.note
        ? `<p style="margin:0;font-family:${FONT_BODY};font-size:13px;line-height:1.5;color:${CAMPAIGN_COLORS.body};">${escapeHtml(it.note)}</p>`
        : "";
      return `<td width="${width}%" valign="top" style="padding:8px 12px 8px 0;">${label}${value}${note}</td>`;
    })
    .join("");
  const inner = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cells}</tr></table>`;
  return row(inner, "8px 40px 20px 40px");
}

function renderButton(block) {
  const url = sanitizeUrl(block.url) || "https://fiddux.com";
  const align = block.align === "left" ? "left" : "center";
  const inner = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="${align}" style="margin:0 auto 0 ${align === "left" ? "0" : "auto"};">
<tr>
<td align="center" style="background-color:${CAMPAIGN_COLORS.primary};border-radius:4px;">
<a href="${escapeHtml(url)}" target="_blank" style="display:inline-block;padding:14px 28px;font-family:${FONT_BODY};font-size:14px;font-weight:bold;color:${CAMPAIGN_COLORS.buttonText};text-decoration:none;letter-spacing:0.4px;">${escapeHtml(block.label || "Open Fiddux")}</a>
</td>
</tr>
</table>`;
  return row(inner, "8px 40px 24px 40px");
}

function renderImage(block) {
  if (!block.url) return "";
  const img = `<img src="${escapeHtml(block.url)}" alt="${escapeHtml(block.alt || "")}" width="520" style="display:block;width:100%;max-width:520px;height:auto;border:0;" />`;
  const caption = block.caption
    ? `<p style="margin:8px 0 0 0;font-family:${FONT_BODY};font-size:12px;line-height:1.5;color:${CAMPAIGN_COLORS.muted};">${escapeHtml(block.caption)}</p>`
    : "";
  return row(`${img}${caption}`, "8px 40px 16px 40px");
}

function renderDivider() {
  return row(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top:1px solid ${CAMPAIGN_COLORS.divider};font-size:0;line-height:0;height:1px;">&nbsp;</td></tr></table>`,
    "12px 40px"
  );
}

function renderOne(block) {
  switch (block.type) {
    case "hero":
      return renderHero(block);
    case "heading":
      return renderHeading(block);
    case "greeting":
      return renderGreeting(block);
    case "text":
      return renderText(block);
    case "section":
      return renderSection(block);
    case "facts":
      return renderFacts(block);
    case "button":
      return renderButton(block);
    case "image":
      return renderImage(block);
    case "divider":
      return renderDivider();
    case "note":
      return renderNote(block);
    default:
      return "";
  }
}

export function renderCampaignBody(blocks) {
  const validated = validateBlocks(blocks);
  const list = validated.ok ? validated.blocks : [];
  const rows = list.map(renderOne).join("");
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${rows}</table>`;
}

export function firstNameFrom(fullName, email) {
  const n = String(fullName || "").trim();
  if (n) return n.split(/\s+/)[0];
  const local = String(email || "").split("@")[0];
  return local || "Client";
}

export function emptyCampaign() {
  return {
    subject: "",
    eyebrow: "CLIENT UPDATE",
    blocks: [createBlock("greeting"), createBlock("heading"), createBlock("text")],
  };
}
